import {Button} from "../../index";
import {MdClose} from "react-icons/md";
import {useEffect, useState} from "react";
import {useViewerStore} from "../../../store/store";
import {CheckCircleIcon, InfoIcon, WarningIcon, WarningCircleIcon} from "@phosphor-icons/react";
import { ViewerAlert } from "../../../@types/store";
import { AlertType } from "../../../@types/enums";
import { markLightReachWarningDismissedByUser } from "../../../helpers/lightReachWarning";

const Alert = (props: ViewerAlert) => {

    const { title, message, messageType, isCloseAfterDisplay, autoCloseSeconds } = props;

    const [alertIcon, setAlertIcon] = useState(<InfoIcon size={32} />);
    const [alertPosition, setAlertPosition] = useState<{ top: number; left: number } | null>(null);
    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const viewerAlert = useViewerStore(state => state.viewerAlert);
    const isDashboardOpen = useViewerStore((state) => Boolean(state.isDashboardOpen));

    useEffect(() => {

        const toastElement = document.getElementById('three-js-toast');
        if(!toastElement) return;

        const removeClasses = () => {
            toastElement.classList.remove(...['three-positive', 'three-negative', 'three-warning-message', 'three-info']);
        };

        removeClasses();

        switch (messageType) {
            case AlertType.success:
                setAlertIcon(<CheckCircleIcon size={32} />);
                toastElement.classList.add('three-positive');
                break;
            case AlertType.error:
                setAlertIcon(<WarningCircleIcon size={24}/>);
                toastElement.classList.add('three-negative');
                break;
            case AlertType.warning:
                setAlertIcon(<WarningIcon size={32} />);
                toastElement.classList.add('three-warning-message');
                break;
            case AlertType.info:
                setAlertIcon(<InfoIcon size={32} />);
                toastElement.classList.add('three-info');
                break;
            default:
                setAlertIcon(<InfoIcon size={32} />);
                toastElement.classList.add('three-info');
                break;
        }

    }, [viewerAlert]);

    const closeThreeJSAlert = () => {
        // Use explicit removal to avoid immediate LightReach re-sync reopen.
        markLightReachWarningDismissedByUser();
        sceneViewer?.removeViewerAlert?.();
        props.onClose?.();
    }


    useEffect(() => {
        if(isCloseAfterDisplay){
            setTimeout(()=>{
                sceneViewer.viewerAlert({show: false});
            }, autoCloseSeconds);
        }
    }
    , []);

    useEffect(() => {
        const updateAlertPosition = () => {
            if (useViewerStore.getState().isDashboardOpen) {
                setAlertPosition(null);
                return;
            }
            const canvasContainer = document.getElementById("canvasContainer");
            if (!canvasContainer) {
                setAlertPosition(null);
                return;
            }
            const canvasRect = canvasContainer.getBoundingClientRect();
            setAlertPosition({
                top: Math.max(canvasRect.top + 12, 8),
                left: canvasRect.left + canvasRect.width / 2,
            });
        };

        updateAlertPosition();
        window.addEventListener("resize", updateAlertPosition);
        return () => window.removeEventListener("resize", updateAlertPosition);
    }, [viewerAlert.show, isDashboardOpen]);


    return (
        <div
            id={"three-js-toast"}
            className={`alert ${viewerAlert && 'open'} three-warning${isDashboardOpen ? ' alert--dashboard-open' : ''}`}
            style={
                !isDashboardOpen && alertPosition
                    ? { top: `${alertPosition.top}px`, left: `${alertPosition.left}px` }
                    : undefined
            }
        >
            <div className='alert__header alert-container'>

                <div className='alert__header--column'>
                    {alertIcon}
                    <span className='alert__title'>{title}</span>
                </div>
                <Button variant='btn-clear' size='btn-resize' className='alert__close' onClick={closeThreeJSAlert}>
                    <MdClose size={24}/>
                </Button>

            </div>
            <p style={{whiteSpace: "pre-line"}} className='alert__content'>{message}</p>
        </div>
    )
}


export default Alert;